// ------------------------------------------------------------
// carSheets/exportUtils.js
// ------------------------------------------------------------
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
// =====================================================
// Global helper: write AED amounts but show "-" for zero/empty
// =====================================================
const AED_NUMFMT = '"AED" #,##0.00;[Red]-"AED" #,##0.00';

function setAedCell(cell, value, { dashZero = true } = {}) {
  const raw = value;

  // Convert strings like "AED 0.00" / "0.00" / "1,234.50" into numbers
  let num;
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/AED/gi, '').replace(/,/g, '').trim();
    num = cleaned === '' ? NaN : Number(cleaned);
  } else {
    num = Number(raw);
  }

  const isEmpty =
    raw === null ||
    raw === undefined ||
    raw === '' ||
    (dashZero && !Number.isNaN(num) && num === 0);

  if (isEmpty) {
    cell.value = '-';
    cell.numFmt = '@'; // text
    cell.alignment = { horizontal: 'right' };
    return;
  }

  cell.value = Number.isNaN(num) ? raw : num;
  cell.numFmt = AED_NUMFMT;
  cell.alignment = { horizontal: 'right' };
}


function normalizeExpenseLabel(value) {
  return String(value || "").toUpperCase().replace(/\s+/g, " " ).trim();
}

function joinCleanParts(parts) {
  return (parts || [])
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .filter((x, idx, arr) => arr.indexOf(x) === idx)
    .join(" | " );
}

function looksLikeDriverAmountOnly(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  // Examples we do NOT want in driver download detail:
  // "RAJEEV RAMA ... (34/36): AED 8,200.00"
  // "SHAKIR IJAZ AHMED: AED 2,800.00"
  // These repeat the driver name and final amount; the amount already appears in
  // the Amount column and the driver name is already shown in the assignment area.
  const hasUsefulCalcWord = /\b(Base|Relief|Discount|Extra|Override|Preview|As of|days|VAT|txn|transaction)\b/i.test(text);
  if (hasUsefulCalcWord) return false;

  return /:\s*AED\s*-?[\d,]+(?:\.\d{1,2})?\s*$/i.test(text);
}

function cleanMonthlyFixedExportDetail(row) {
  const installmentNo = joinCleanParts([
    row.installmentNo,
    row.installmentLabel,
    row.col1,
  ]);
  const escapedInstallmentNo = installmentNo
    ? installmentNo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    : "";

  const rawParts = [
    row.autoDetails,
    row.calculationDetails,
    row.detail,
    row.description,
  ]
    .flatMap((value) => String(value || "").split(";"))
    .map((x) => x.trim())
    .filter(Boolean);

  const cleanedParts = rawParts
    .map((part) => {
      let text = String(part || "").trim();
      if (!text) return "";

      if (looksLikeDriverAmountOnly(text)) return "";

      // Do not show the word "Installment" in the driver download sheet.
      // Keep the number itself once, e.g. "34/36" or "9.5/36", then show the calculation.
      text = text.replace(/^Installment\s+[0-9]+(?:\.\d+)?(?:\/[0-9]+(?:\.\d+)?)?\s*:\s*/i, "");

      // "Installment 34/36, remaining 2" or "9.5/36, remaining 26.5" is internal/admin info; remove it.
      if (/^(?:Installment\s+)?[0-9]+(?:\.\d+)?(?:\/[0-9]+(?:\.\d+)?)?\s*,?\s*remaining\b/i.test(text)) return "";

      // Fallback cleanup if any old detail text still starts with the word.
      text = text.replace(/^Installment\s+/i, "");

      // If an old saved detail starts with the installment number, remove that prefix.
      // "9.5/36: Base AED ..." -> "Base AED ..."
      if (escapedInstallmentNo) {
        const prefixRe = new RegExp(`^${escapedInstallmentNo}\\s*:?\\s*`, "i");
        text = text.replace(prefixRe, "").trim();
      }

      // Avoid repeating the number by itself because it is already added first.
      if (installmentNo && text === installmentNo) return "";

      return text.trim();
    })
    .filter(Boolean);

  return joinCleanParts([installmentNo, ...cleanedParts]);
}

function getExpenseDownloadDetail(row) {
  if (!row) return "";

  const label = normalizeExpenseLabel(row.label);
  const isMonthlyFixed = label === "MONTHLY FIXED COST" || row.autoDriverCost;

  // Driver/downloaded statements should show the installment number and
  // calculation clearly, but without repeating driver names, final amount, or
  // the word "Installment" multiple times.
  if (isMonthlyFixed) {
    return cleanMonthlyFixedExportDetail(row);
  }

  return joinCleanParts([row.description]);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function makeUniqueWorksheetName(workbook, baseName) {
  const clean = String(baseName || "Sheet")
    .replace(/[\\\/\?\*\[\]]/g, " ")
    .replace(/:+/g, " ")
    .trim();

  const base = clean.slice(0, 31) || "Sheet";

  const existing = new Set(workbook.worksheets.map((w) => w.name));
  if (!existing.has(base)) return base;

  // leave space for " - 1"
  const trimmed = base.slice(0, 31 - 4);

  let i = 1;
  while (true) {
    const candidate = `${trimmed} - ${i}`.slice(0, 31);
    if (!existing.has(candidate)) return candidate;
    i += 1;
  }
}

export async function exportBalancesByTab(sheets, month, tabName = "combined") {
  try {
    const workbook = new ExcelJS.Workbook();
    
    // Format month for display
    const [year, monthNum] = month.split('-');
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthDisplay = `${monthNames[parseInt(monthNum) - 1]} ${year}`;
    
    // Create sheet name
    let sheetName = "";
    let mainHeader = "";
    let typeHeader = "";
    
    switch(tabName) {
      case "lolia":
        sheetName = "LOLIA_FIXED";
        mainHeader = "LOLIA LIMOUSINE FOR LUXURY MOTOR VEHICLES SERVICES LLC";
        typeHeader = "FIXED BASE DRIVERS PAYABLE & RECEIVABLE";
        break;
      case "cll":
        sheetName = "COMFORT_LINE_FIXED";
        mainHeader = "COMFORT LINE LUXURY MOTOR VEHICLES SERVICES LLC";
        typeHeader = "FIXED BASE DRIVERS PAYABLE & RECEIVABLE";
        break;
      case "salary":
        sheetName = "COMMISSION_SALARY";
        mainHeader = "LOLIA LIMOUSINE & COMFORT LINE";
        typeHeader = "SALARY & COMMISSION DRIVERS PAYABLE & RECEIVABLE";
        break;
      default:
        sheetName = "ALL_CATEGORIES";
        mainHeader = "LOLIA LIMOUSINE & COMFORT LINE";
        typeHeader = "ALL DRIVERS PAYABLE & RECEIVABLE";
        break;
    }
    
    const ws = workbook.addWorksheet(sheetName);

    // ============================================
    // MAIN HEADERS
    // ============================================
    
    // Row 1: COMPANY HEADER
    ws.getCell('A1').value = mainHeader;
    ws.getCell('A1').font = { bold: true, size: 16 };
    ws.getCell('A1').alignment = { horizontal: 'center' };
    ws.mergeCells('A1:I1');
    
    // Row 2: TYPE HEADER
    ws.getCell('A2').value = typeHeader;
    ws.getCell('A2').font = { bold: true, size: 14 };
    ws.getCell('A2').alignment = { horizontal: 'center' };
    ws.mergeCells('A2:I2');
    
    // Row 3: MONTH HEADER
    ws.getCell('A3').value = `FOR THE MONTH OF ${monthDisplay.toUpperCase()}`;
    ws.getCell('A3').font = { bold: true, size: 12, color: { argb: 'FF333333' } };
    ws.getCell('A3').alignment = { horizontal: 'center' };
    ws.mergeCells('A3:I3');
    
    // Empty row for spacing
    ws.addRow([]);
    
    // ============================================
    // TABLE HEADERS (Row 5)
    // ============================================
    const headers = [
      "VEHICLE", "NAME", "COMPANY", "PAYABLE GROSS", 
      "DEDUCTIONS", "NET PAYABLE", "RECEIVABLE", 
      "IBAN NUMBER", "BANK NAME"
    ];
    
    const headerRow = ws.addRow(headers);
    
    // Style header row
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2E75B6' }
    };
    headerRow.alignment = { 
      horizontal: 'center', 
      vertical: 'middle',
      wrapText: true
    };
    headerRow.height = 30;
    
    // Set column widths
    ws.columns = [
      { width: 12 }, // A: VEHICLE
      { width: 25 }, // B: NAME
      { width: 25 }, // C: COMPANY
      { width: 12 }, // D: PAYABLE GROSS
      { width: 12 }, // E: DEDUCTIONS
      { width: 12 }, // F: NET PAYABLE
      { width: 12 }, // G: RECEIVABLE
      { width: 25 }, // H: IBAN NUMBER
      { width: 20 }  // I: BANK NAME
    ];

    // ============================================
    // PROCESS EACH SHEET
    // ============================================
    let totalPayableGross = 0;
    let totalDeductions = 0;
    let totalReceivable = 0;
    
    sheets.forEach((sheet, index) => {
      if (!sheet || sheet.id === "summary") return;

      // Find the "BALANCE PAYABLE / RECEIVABLE" row
      let balancePayableReceivable = 0;
      
      if (sheet.incomeRows && sheet.incomeRows.length > 0) {
        const balanceRow = sheet.incomeRows.find(row => 
          row.rowType === "summary" && 
          row.label && 
          row.label.toUpperCase().includes("BALANCE PAYABLE / RECEIVABLE")
        );
        
        if (balanceRow && typeof balanceRow.total === 'number') {
          balancePayableReceivable = balanceRow.total;
        }
      }

      // Get driver name
      let driverName = "";
      if (sheet.drivers && sheet.drivers.length > 0) {
        const validDrivers = sheet.drivers.filter(d => d && d.trim() !== "");
        if (validDrivers.length > 0) {
          driverName = validDrivers[0];
        }
      }

      // Determine payable/receivable
      let payableGross = 0;
      let deductions = 0;
      let payableNet = 0;
      let receivable = 0;

      if (balancePayableReceivable > 0) {
        payableGross = balancePayableReceivable;
        payableNet = balancePayableReceivable;
        receivable = 0;
        totalPayableGross += payableGross;
      } else if (balancePayableReceivable < 0) {
        payableGross = 0;
        payableNet = 0;
        receivable = Math.abs(balancePayableReceivable);
        totalReceivable += receivable;
      }

      // Add data row (row number: 6 + index)
      const rowNumber = 6 + index;
      
      // Set cell values directly
      ws.getCell(`A${rowNumber}`).value = sheet.carNumber || "";
      ws.getCell(`B${rowNumber}`).value = driverName;
      ws.getCell(`C${rowNumber}`).value = sheet.company || "";
      ws.getCell(`D${rowNumber}`).value = payableGross;
      ws.getCell(`E${rowNumber}`).value = deductions;
      
      // Set NET PAYABLE formula
      ws.getCell(`F${rowNumber}`).value = { 
        formula: `D${rowNumber}-E${rowNumber}`,
        result: payableGross - deductions
      };
      
      ws.getCell(`G${rowNumber}`).value = receivable;
      ws.getCell(`H${rowNumber}`).value = "";
      ws.getCell(`I${rowNumber}`).value = "";
      
      // Format amount cells
      ['D', 'E', 'F', 'G'].forEach(col => {
        const cell = ws.getCell(`${col}${rowNumber}`);
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right' };
      });
    });

    // ============================================
    // TOTALS ROW
    // ============================================
    const totalRowNumber = 6 + sheets.filter(s => s && s.id !== "summary").length;
    
    // Add totals row
    ws.getCell(`A${totalRowNumber}`).value = "TOTAL";
    ws.getCell(`A${totalRowNumber}`).font = { bold: true };
    ws.getCell(`B${totalRowNumber}`).value = "";
    ws.getCell(`C${totalRowNumber}`).value = "";
    ws.getCell(`D${totalRowNumber}`).value = totalPayableGross;
    ws.getCell(`E${totalRowNumber}`).value = totalDeductions;
    
    // Calculate total NET PAYABLE
    const totalNetPayable = totalPayableGross - totalDeductions;
    ws.getCell(`F${totalRowNumber}`).value = totalNetPayable;
    
    ws.getCell(`G${totalRowNumber}`).value = totalReceivable;
    ws.getCell(`H${totalRowNumber}`).value = "";
    ws.getCell(`I${totalRowNumber}`).value = "";

    // Style totals row
    for (let i = 1; i <= 9; i++) {
      const cell = ws.getCell(totalRowNumber, i);
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2F2F2' }
      };
    }
    
    // Format total amounts
    ['D', 'E', 'F', 'G'].forEach(col => {
      const cell = ws.getCell(`${col}${totalRowNumber}`);
      cell.numFmt = '#,##0.00';
      cell.alignment = { horizontal: 'right' };
    });

    // ============================================
    // APPLY COLORS TO SPECIFIC COLUMNS
    // ============================================
    
    // Color for NET PAYABLE column (Column F) - GREEN
    for (let i = 6; i <= totalRowNumber; i++) {
      const netCell = ws.getCell(`F${i}`);
      if (i === totalRowNumber) {
        // Totals row - light green
        netCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFC6E0B4' }
        };
      } else {
        // Data rows - very light green
        netCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE2EFDA' }
        };
      }
      netCell.font = { bold: true };
    }

    // Color for RECEIVABLE column (Column G) - ORANGE
    for (let i = 6; i <= totalRowNumber; i++) {
      const receivableCell = ws.getCell(`G${i}`);
      if (i === totalRowNumber) {
        // Totals row - light orange
        receivableCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8CBAD' }
        };
      } else {
        // Data rows - very light orange
        receivableCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFCE4D6' }
        };
      }
    }

    // ============================================
    // ADD BORDERS TO TABLE
    // ============================================
    // Table from row 5 (header) to totals row
    for (let i = 5; i <= totalRowNumber; i++) {
      for (let j = 1; j <= 9; j++) {
        const cell = ws.getCell(i, j);
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      }
    }

    // ============================================
    // FREEZE PANES (Header row stays visible)
    // ============================================
    ws.views = [
      { state: 'frozen', xSplit: 0, ySplit: 5, activeCell: 'A6', showGridLines: true }
    ];

    // ============================================
    // SET PRINT SETTINGS
    // ============================================
    ws.pageSetup = {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.4, right: 0.4,
        top: 0.5, bottom: 0.5,
        header: 0.3, footer: 0.3
      },
      printTitlesRow: '5:5'
    };

    // ============================================
    // ADD INSTRUCTIONS
    // ============================================
    const instructionRow = totalRowNumber + 2;
    ws.getCell(`A${instructionRow}`).value = "INSTRUCTIONS:";
    ws.getCell(`A${instructionRow}`).font = { bold: true, color: { argb: 'FF666666' } };
    
    ws.getCell(`A${instructionRow + 1}`).value = "1. Fill IBAN NUMBER and BANK NAME columns manually";
    ws.getCell(`A${instructionRow + 2}`).value = "2. Add any DEDUCTIONS in the DEDUCTIONS column";
    ws.getCell(`A${instructionRow + 3}`).value = "3. NET PAYABLE column automatically calculates: GROSS - DEDUCTIONS";
    ws.getCell(`A${instructionRow + 4}`).value = "4. All amounts are in AED";

    // ============================================
    // SAVE FILE
    // ============================================
    const buffer = await workbook.xlsx.writeBuffer();
    
    let fileNamePrefix = "";
    switch(tabName) {
      case "lolia": fileNamePrefix = "LOLIA_FIXED"; break;
      case "cll": fileNamePrefix = "COMFORT_LINE_FIXED"; break;
      case "salary": fileNamePrefix = "COMMISSION_SALARY"; break;
      default: fileNamePrefix = "ALL_CATEGORIES"; break;
    }
    
    const fileName = `${fileNamePrefix}_Payable_Receivable_${month}.xlsx`;
    saveAs(new Blob([buffer]), fileName);

    console.log(`✅ Exported ${tabName} balance sheet with ${sheets.length} cars`);
    return true;

  } catch (error) {
    console.error("Balance export error:", error);
    throw error;
  }
}
// ------------------------------------------------------------
// PDF Export
// ------------------------------------------------------------
export async function exportSheetToPDF(sheet, month) {
  try {
    // Create a hidden container for the sheet
    const container = document.createElement('div');
    container.id = 'pdf-export-container';
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '860px'; // narrower layout so one-page PDF uses more vertical space and text is larger
    container.style.backgroundColor = 'white';
    container.style.padding = '10px';
    container.style.boxSizing = 'border-box';
    container.style.fontFamily = 'Arial, sans-serif';
    
    // Add container to document
    document.body.appendChild(container);
    
    // Create PDF content
    container.innerHTML = generatePDFContent(sheet, month);
    
    // Wait for images to load
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Convert to canvas
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    
    // Remove container
    document.body.removeChild(container);
    
    // Create PDF as ONE PAGE ONLY.
    // We fit the complete rendered statement into a single A4 page instead of
    // cropping or splitting it into multiple pages.
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 4;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;

    const imgRatio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
    const imgWidth = canvas.width * imgRatio;
    const imgHeight = canvas.height * imgRatio;
    const x = margin + (maxWidth - imgWidth) / 2;
    const y = margin;

    // Add full statement image fitted to one page.
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);
    
    // Save PDF
    const fileName = `${sheet.carNumber}_${month}_${Date.now()}.pdf`;
    pdf.save(fileName);
    
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
}

function generatePDFContent(sheet, month) {
  return `
    <style>
      #pdf-one-page-statement, #pdf-one-page-statement * { box-sizing: border-box; }
      #pdf-one-page-statement { font-size: 11.8px; line-height: 1.12; color: #222; }
      #pdf-one-page-statement h1 { font-size: 19px !important; margin: 0 !important; }
      #pdf-one-page-statement h2 { font-size: 13px !important; margin: 2px 0 !important; }
      #pdf-one-page-statement h3 { font-size: 12.5px !important; padding: 3px 5px !important; margin: 0 0 3px 0 !important; }
      #pdf-one-page-statement table { page-break-inside: avoid; }
      #pdf-one-page-statement th,
      #pdf-one-page-statement td { padding: 2px 3px !important; line-height: 1.12 !important; }
      #pdf-one-page-statement .pdf-section { margin-bottom: 5px !important; }
      #pdf-one-page-statement .pdf-summary { padding: 8px !important; }
    </style>
    <div id="pdf-one-page-statement" style="padding: 8px;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 8px; border-bottom: 2px solid #333; padding-bottom: 5px;">
        <h1 style="margin: 0; color: #333; font-size: 19px;">Car Profit & Loss Statement</h1>
        <h2 style="margin: 2px 0; color: #666; font-size: 13px;">${sheet.monthLabel || month}</h2>
      </div>
      
      <!-- Car Details -->
      <div class="pdf-section" style="margin-bottom: 8px;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 4px;">
          <tr>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Car Number:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.carNumber || 'N/A'}</td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Car Model:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.carModel || 'N/A'}</td>
          </tr>
          <tr>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Company:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.company || 'N/A'}</td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Period:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.periodLabel || 'Full Month'}</td>
          </tr>
          <tr>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Plan Type:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.planType || 'N/A'}</td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Drivers:</strong></td>
            <td style="padding: 5px; border-bottom: 1px solid #ddd;">${sheet.drivers?.join(', ') || 'N/A'}</td>
          </tr>
        </table>
      </div>
      
      <!-- Expenses Section -->
      <div class="pdf-section" style="margin-bottom: 8px;">
        <h3 style="background: #f0f0f0; padding: 8px; margin: 0 0 10px 0;">EXPENSES</h3>
        ${generateExpensesTableHTML(sheet)}
      </div>

      <!-- Income Section -->
      <div class="pdf-section" style="margin-bottom: 8px;">
        <h3 style="background: #f0f0f0; padding: 8px; margin: 0 0 10px 0;">INCOME</h3>
        ${generateIncomeTableHTML(sheet)}
      </div>
      
      <!-- Cash Section -->
      <div class="pdf-section" style="margin-bottom: 8px;">
        <h3 style="background: #f0f0f0; padding: 8px; margin: 0 0 10px 0;">CASH TRIPS</h3>
        ${generateCashTableHTML(sheet)}
      </div>
      
    </div>
  `;
}

function generateIncomeTableHTML(sheet) {
  if (!sheet.incomeRows || sheet.incomeRows.length === 0) {
    return '<p style="color: #666;">No income data</p>';
  }
  
  let html = `
    <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
      <thead>
        <tr style="background: #e9ecef;">
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Platform</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Driver</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 1</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 2</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 3</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 4</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 5</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  sheet.incomeRows.forEach(row => {
    if (row.rowType === 'summary') {
      html += `
        <tr style="background: #f8f9fa; font-weight: bold;">
          <td colspan="7" style="padding: 8px; border: 1px solid #ddd;">${row.label || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">
            ${typeof row.total === 'number' ? `AED ${row.total.toFixed(2)}` : row.total || ''}
          </td>
        </tr>
      `;
    } else {
      html += `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;">${row.company || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${row.driver || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week1 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week2 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week3 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week4 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week5 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">
            ${(row.total || 0).toFixed(2)}
          </td>
        </tr>
      `;
    }
  });
  
  html += `</tbody></table>`;
  return html;
}

function generateCashTableHTML(sheet) {
  if (!sheet.cashRows || sheet.cashRows.length === 0) {
    return '<p style="color: #666;">No cash data</p>';
  }
  
  let html = `
    <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
      <thead>
        <tr style="background: #e9ecef;">
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Platform</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Driver</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 1</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 2</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 3</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 4</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Week 5</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  sheet.cashRows.forEach(row => {
    if (row.rowType === 'summary') {
      html += `
        <tr style="background: #f8f9fa; font-weight: bold;">
          <td colspan="7" style="padding: 8px; border: 1px solid #ddd;">${row.label || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">
            ${typeof row.total === 'number' ? `AED ${row.total.toFixed(2)}` : row.total || ''}
          </td>
        </tr>
      `;
    } else {
      html += `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;">${row.company || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${row.driver || ''}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week1 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week2 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week3 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week4 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(row.week5 || 0).toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">
            ${(row.total || 0).toFixed(2)}
          </td>
        </tr>
      `;
    }
  });
  
  html += `</tbody></table>`;
  return html;
}

function generateExpensesTableHTML(sheet) {
  if (!sheet.expensesRows || sheet.expensesRows.length === 0) {
    return '<p style="color: #666;">No expenses data</p>';
  }
  
  let html = `
    <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
      <thead>
        <tr style="background: #e9ecef;">
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left; width: 28%;">Expense Item</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left; width: 52%;">Details</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: right; width: 20%;">Amount</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  sheet.expensesRows.forEach(row => {
    if (row.isSummary) {
      html += `
        <tr style="background: #f8f9fa; font-weight: bold;">
          <td colspan="2" style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(row.label || '')}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right; color: #d32f2f;">
            AED ${(row.total || 0).toFixed(2)}
          </td>
        </tr>
      `;
    } else {
      const detail = getExpenseDownloadDetail(row);
      html += `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(row.label || '')}</td>
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 11px; color: #333;">${escapeHtml(detail)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">
            ${(row.total || 0).toFixed(2)}
          </td>
        </tr>
      `;
    }
  });
  
  html += `</tbody></table>`;
  return html;
}

// ------------------------------------------------------------
// Excel Export - FIXED VERSION
// ------------------------------------------------------------
export async function exportSheetToExcel(sheet, month) {
  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Car Management System';
    workbook.created = new Date();
    
    // Main Sheet
    const mainSheet = workbook.addWorksheet('Car P&L Statement');
    
    // Formatting styles
    const headerStyle = { 
      font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
    
    const subHeaderStyle = {
      font: { bold: true, size: 12, color: { argb: 'FF333333' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
    
    const titleStyle = {
      font: { bold: true, size: 16, color: { argb: 'FF333333' } },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
    
    const tableHeaderStyle = {
      font: { bold: true, color: { argb: 'FF333333' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } },
      border: {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      }
    };
    
    const currencyStyle = {
      numFmt: '"AED" #,##0.00;[Red]"AED" -#,##0.00'
    };


      
    // ============================================
    // HEADER SECTION
    // ============================================
    let currentRow = 1;
    
    // Title
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    mainSheet.getCell(`A${currentRow}`).value = 'CAR PROFIT & LOSS STATEMENT';
    mainSheet.getCell(`A${currentRow}`).style = titleStyle;
    currentRow++;
    
    // Month
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    mainSheet.getCell(`A${currentRow}`).value = sheet.monthLabel || month;
    mainSheet.getCell(`A${currentRow}`).style = { ...titleStyle, font: { ...titleStyle.font, size: 12 } };
    currentRow += 2; // Add extra space
    
    // ============================================
    // CAR DETAILS SECTION - FIXED DRIVER DISPLAY
    // ============================================
    mainSheet.getCell(`A${currentRow}`).value = 'Car Details';
    mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    currentRow++;
    
    // Car Details Table - Fixed to show drivers properly
    const carDetails = [
      ['Car Number:', sheet.carNumber || 'N/A', 'Car Model:', sheet.carModel || 'N/A'],
      ['Company:', sheet.company || 'N/A', 'Plan Type:', sheet.planType || 'N/A']
    ];
    
    carDetails.forEach((detail, index) => {
      mainSheet.getCell(`A${currentRow + index}`).value = detail[0];
      mainSheet.getCell(`A${currentRow + index}`).style = { font: { bold: true } };
      mainSheet.getCell(`B${currentRow + index}`).value = detail[1];
      
      mainSheet.getCell(`E${currentRow + index}`).value = detail[2];
      mainSheet.getCell(`E${currentRow + index}`).style = { font: { bold: true } };
      mainSheet.getCell(`F${currentRow + index}`).value = detail[3];
    });
    
    currentRow += carDetails.length + 1; // Move to next row
    
    // ============================================
    // DRIVER ASSIGNMENT SECTION - FIXED
    // ============================================
    if (sheet.driverPeriods && Object.keys(sheet.driverPeriods).length > 0) {
      // Driver Assignment Header
      mainSheet.getCell(`A${currentRow}`).value = 'Driver Assignment Periods';
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      currentRow++;
      
      // Driver Periods Table Headers
      const driverHeaders = ['Driver ID', 'Driver Name', 'Start Date', 'End Date', 'Days Worked'];
      driverHeaders.forEach((header, colIndex) => {
        const cell = mainSheet.getCell(`${String.fromCharCode(65 + colIndex)}${currentRow}`);
        cell.value = header;
        cell.style = tableHeaderStyle;
      });
      currentRow++;
      
      // Add each driver's period (CLAMPED to sheet month)
{
  // month boundaries (expects month like "2025-12")
  const [yy, mm] = String(month || "").split("-");
  const sheetStart = new Date(Number(yy), Number(mm) - 1, 1);
  const sheetEnd = new Date(Number(yy), Number(mm), 0);

  sheetStart.setHours(0, 0, 0, 0);
  sheetEnd.setHours(23, 59, 59, 999);

  Object.entries(sheet.driverPeriods || {}).forEach(([driverId, periods]) => {
    const list = Array.isArray(periods) ? periods : [periods];

    list.forEach((period) => {
      const startV = period?.startDate || period?.start;
      const endV = period?.endDate || period?.end;

      // raw -> Date
      let s = startV ? new Date(startV) : null;
      let e = endV ? new Date(endV) : null;

      // fallback if missing/invalid
      if (!s || isNaN(s.getTime())) s = new Date(sheetStart);
      if (!e || isNaN(e.getTime())) e = new Date(sheetEnd);

      // clamp into this sheet month
      if (s < sheetStart) s = new Date(sheetStart);
      if (e > sheetEnd) e = new Date(sheetEnd);

      // IMPORTANT: write at noon to avoid Excel timezone shifting one day back
      s.setHours(12, 0, 0, 0);
      e.setHours(12, 0, 0, 0);

      mainSheet.getCell(`A${currentRow}`).value = driverId;
      mainSheet.getCell(`B${currentRow}`).value = period?.driverName || driverId;

      mainSheet.getCell(`C${currentRow}`).value = s;
      mainSheet.getCell(`C${currentRow}`).numFmt = "dd-mmm-yyyy";

      mainSheet.getCell(`D${currentRow}`).value = e;
      mainSheet.getCell(`D${currentRow}`).numFmt = "dd-mmm-yyyy";

      // days inclusive (safe)
      const days = Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
      mainSheet.getCell(`E${currentRow}`).value = Math.max(0, days);

      currentRow++;
    });
  });
}

      
      currentRow += 2; // Add space after driver periods
    }
    
     // ============================================
    // EXPENSES SECTION
    // ============================================
    mainSheet.getCell(`A${currentRow}`).value = 'EXPENSES';
    mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    currentRow++;
    
    // Expenses Table Headers
    const expenseHeaders = [
  'Expense Item', // A
  '',             // B
  '',             // C
  '',             // D
  '',             // E
  '',             // F
  '',             // G
  'Amount'        // H
];


    expenseHeaders.forEach((header, colIndex) => {
  const colLetter = String.fromCharCode(65 + colIndex); // A..H
  const cell = mainSheet.getCell(`${colLetter}${currentRow}`);
  cell.value = header;
  cell.style = tableHeaderStyle;
});


    currentRow++;
    
    // Expenses Data
    if (sheet.expensesRows && sheet.expensesRows.length > 0) {
      sheet.expensesRows.forEach(row => {
        if (row.isSummary || row.rowType === 'summary') {
          // Summary row
          mainSheet.getCell(`A${currentRow}`).value = row.label || 'TOTAL EXPENSES';
          mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);

          mainSheet.getCell(`A${currentRow}`).style = { 
            font: { bold: true }, 
            alignment: { horizontal: 'right' }
          };
          setAedCell(mainSheet.getCell(`H${currentRow}`), row.total, { dashZero: true });
          mainSheet.getCell(`H${currentRow}`).style = { ...currencyStyle, font: { bold: true, color: { argb: 'FFFF0000' } } };
                } else {

          const label = (row.label || "").toUpperCase().trim();
          const isTraffic = label.includes("TRAFFIC");
          const isFineDate = label.includes("FINE DATE");

          // -----------------------------
          // 2️⃣ TRAFFIC FINES / FINE DATE
          // -----------------------------
          if (isTraffic || isFineDate) {
            mainSheet.getCell(`A${currentRow}`).value = row.label || "";

            // Export 10 fine columns
            // We only want to export the first 7 fine columns into B..H
for (let i = 1; i <= 6; i++) {
  const colLetter = String.fromCharCode(65 + i); // B..G
  mainSheet.getCell(`${colLetter}${currentRow}`).value = row[`col${i}`] || '';
}

// Column H = total (ONLY place where total should appear)
if (isFineDate) {
  // Fine Date is informational text row → keep total blank
  mainSheet.getCell(`H${currentRow}`).value = "";
  mainSheet.getCell(`H${currentRow}`).style = {};
} else {
  setAedCell(mainSheet.getCell(`H${currentRow}`), row.total, { dashZero: true });
}

          } 
          
         // -----------------------------
// 3️⃣ NORMAL EXPENSE ROW
// -----------------------------
else {
  // Column A → Expense Label
  mainSheet.getCell(`A${currentRow}`).value = row.label || "";

  // Description/detail occupies B → G. For MONTHLY FIXED COST, include
  // installment number and calculation details so exported/downloaded sheets
  // show the driver exactly how the amount was calculated.
  mainSheet.mergeCells(`B${currentRow}:G${currentRow}`);
  const detailCell = mainSheet.getCell(`B${currentRow}`);
  detailCell.value = getExpenseDownloadDetail(row);
  detailCell.alignment = { wrapText: true, vertical: "top" };

  // Column H → Total (same as income section)
  setAedCell(mainSheet.getCell(`H${currentRow}`), row.total, { dashZero: true });
}

        }
        currentRow++;
      });
    } else {
      mainSheet.getCell(`A${currentRow}`).value = 'No expenses data';
      mainSheet.mergeCells(`A${currentRow}:L${currentRow}`);

      currentRow++;
    }
    
    currentRow += 2;
    // ============================================
    // INCOME SECTION
    // ============================================
    mainSheet.getCell(`A${currentRow}`).value = 'INCOME';
    mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    currentRow++;
    
    // Income Table Headers
    const incomeHeaders = ['Platform', 'Driver', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Total'];
    incomeHeaders.forEach((header, colIndex) => {
      const cell = mainSheet.getCell(`${String.fromCharCode(65 + colIndex)}${currentRow}`);
      cell.value = header;
      cell.style = tableHeaderStyle;
    });
    currentRow++;
    
    // Income Data
    if (sheet.incomeRows && sheet.incomeRows.length > 0) {
      sheet.incomeRows.forEach(row => {
        if (row.rowType === 'summary') {
          mainSheet.getCell(`A${currentRow}`).value = row.label;
          mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);
          mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }, alignment: { horizontal: 'right' } };
          const hCell = mainSheet.getCell(`H${currentRow}`);
          setAedCell(hCell, row.total, { dashZero: true });
          hCell.font = { ...(hCell.font || {}), bold: true };
        } else {
          mainSheet.getCell(`A${currentRow}`).value = row.company || '';
          mainSheet.getCell(`B${currentRow}`).value = row.driver || '';
          setAedCell(mainSheet.getCell(`C${currentRow}`), row.week1, { dashZero: true });
          setAedCell(mainSheet.getCell(`D${currentRow}`), row.week2, { dashZero: true });
          setAedCell(mainSheet.getCell(`E${currentRow}`), row.week3, { dashZero: true });
          setAedCell(mainSheet.getCell(`F${currentRow}`), row.week4, { dashZero: true });
          setAedCell(mainSheet.getCell(`G${currentRow}`), row.week5, { dashZero: true });
          setAedCell(mainSheet.getCell(`H${currentRow}`), row.total, { dashZero: true });
          mainSheet.getCell(`H${currentRow}`).style = { ...currencyStyle, font: { bold: true } };
        }
        currentRow++;
      });
    } else {
      mainSheet.getCell(`A${currentRow}`).value = 'No income data';
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      currentRow++;
    }
    
    currentRow += 2;
    
    // ============================================
    // CASH TRIPS SECTION
    // ============================================
    mainSheet.getCell(`A${currentRow}`).value = 'CASH TRIPS';
    mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
    mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
    currentRow++;
    
    // Cash Table Headers
    const cashHeaders = ['Platform', 'Driver', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Total'];
    cashHeaders.forEach((header, colIndex) => {
      const cell = mainSheet.getCell(`${String.fromCharCode(65 + colIndex)}${currentRow}`);
      cell.value = header;
      cell.style = tableHeaderStyle;
    });
    currentRow++;
    
    // Cash Data
    if (sheet.cashRows && sheet.cashRows.length > 0) {
      sheet.cashRows.forEach(row => {
        if (row.rowType === 'summary') {
          mainSheet.getCell(`A${currentRow}`).value = row.label;
          mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);
          mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }, alignment: { horizontal: 'right' } };
          const hCell = mainSheet.getCell(`H${currentRow}`);
          setAedCell(hCell, row.total, { dashZero: true });
          hCell.font = { ...(hCell.font || {}), bold: true };
        } else {
          mainSheet.getCell(`A${currentRow}`).value = row.company || '';
          mainSheet.getCell(`B${currentRow}`).value = row.driver || '';
          setAedCell(mainSheet.getCell(`C${currentRow}`), row.week1, { dashZero: true });
          setAedCell(mainSheet.getCell(`D${currentRow}`), row.week2, { dashZero: true });
          setAedCell(mainSheet.getCell(`E${currentRow}`), row.week3, { dashZero: true });
          setAedCell(mainSheet.getCell(`F${currentRow}`), row.week4, { dashZero: true });
          setAedCell(mainSheet.getCell(`G${currentRow}`), row.week5, { dashZero: true });
          setAedCell(mainSheet.getCell(`H${currentRow}`), row.total, { dashZero: true });
          mainSheet.getCell(`H${currentRow}`).style = { ...currencyStyle, font: { bold: true } };
        }
        currentRow++;
      });
    } else {
      mainSheet.getCell(`A${currentRow}`).value = 'No cash data';
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      currentRow++;
    }
    
    currentRow += 2;
    
       
   // ============================================

    // (SUMMARY section removed - values were confusing when empty)

// ADJUST COLUMN WIDTHS - FIXED FOR DRIVER NAMES
    // ============================================
    mainSheet.columns = [
      { width: 25 }, // A: Platform/Item (wider for driver names)
      { width: 25 }, // B: Driver Name (wider for long names)
      { width: 12 }, // C: Week 1
      { width: 12 }, // D: Week 2
      { width: 12 }, // E: Week 3
      { width: 12 }, // F: Week 4
      { width: 12 }, // G: Week 5
      { width: 15 }  // H: Total
    ];
    
    // Enable text wrapping for driver names
    mainSheet.getColumn('B').alignment = { wrapText: true, vertical: 'middle' };
    
    // Set print area to ensure everything fits
    mainSheet.pageSetup = {
      printArea: 'A1:H' + (currentRow + 2),
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      orientation: 'landscape' // Use landscape for wider content
    };
    
    // Add borders around main content
    for (let i = 1; i <= currentRow; i++) {
      for (let j = 1; j <= 8; j++) {
        const cell = mainSheet.getCell(i, j);
        if (!cell.style.border) {
          cell.style.border = {
            top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            right: { style: 'thin', color: { argb: 'FFDDDDDD' } }
          };
        }
      }
    }
    
    // ============================================
    // ADD DRIVER PERIODS DETAIL SHEET (Optional)
    // ============================================
    if (sheet.driverPeriods && Object.keys(sheet.driverPeriods).length > 0) {
      const driverSheet = workbook.addWorksheet('Driver Periods Detail');
      
      driverSheet.getCell('A1').value = `Driver Assignment Periods - ${sheet.carNumber}`;
      driverSheet.getCell('A1').style = titleStyle;
      driverSheet.mergeCells('A1:E1');
      
      const driverHeaders = ['Driver ID', 'Driver Name', 'Start Date', 'End Date', 'Days Worked'];
      driverHeaders.forEach((header, colIndex) => {
        driverSheet.getCell(`${String.fromCharCode(65 + colIndex)}3`).value = header;
        driverSheet.getCell(`${String.fromCharCode(65 + colIndex)}3`).style = tableHeaderStyle;
      });
      
      let driverRow = 4;

// month boundaries (expects month like "2025-12")
const [yy, mm] = String(month || "").split("-");
const sheetStart = new Date(Number(yy), Number(mm) - 1, 1);
const sheetEnd = new Date(Number(yy), Number(mm), 0);

sheetStart.setHours(0, 0, 0, 0);
sheetEnd.setHours(23, 59, 59, 999);

Object.entries(sheet.driverPeriods || {}).forEach(([driverId, periodsOrObj]) => {
  const periods = Array.isArray(periodsOrObj) ? periodsOrObj : [periodsOrObj];

  periods.forEach((period) => {
    if (!period) return;

    const startV = period.startDate || period.start;
    const endV = period.endDate || period.end;

    // raw -> Date
    let s = startV ? new Date(startV) : null;
    let e = endV ? new Date(endV) : null;

    // fallback if missing/invalid
    if (!s || isNaN(s.getTime())) s = new Date(sheetStart);
    if (!e || isNaN(e.getTime())) e = new Date(sheetEnd);

    // clamp into this sheet month
    if (s < sheetStart) s = new Date(sheetStart);
    if (e > sheetEnd) e = new Date(sheetEnd);

    // IMPORTANT: set noon to avoid Excel timezone shifting date
    s.setHours(12, 0, 0, 0);
    e.setHours(12, 0, 0, 0);

    driverSheet.getCell(`A${driverRow}`).value = driverId;
    driverSheet.getCell(`B${driverRow}`).value = period.driverName || driverId;

    driverSheet.getCell(`C${driverRow}`).value = s;
    driverSheet.getCell(`C${driverRow}`).numFmt = "dd-mmm-yyyy";

    driverSheet.getCell(`D${driverRow}`).value = e;
    driverSheet.getCell(`D${driverRow}`).numFmt = "dd-mmm-yyyy";

    const days = Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
    driverSheet.getCell(`E${driverRow}`).value = Math.max(0, days);

    driverRow++;
  });
});

      
      driverSheet.columns = [
        { width: 25 },
        { width: 30 },
        { width: 15 },
        { width: 15 },
        { width: 12 }
      ];
    }
    
    // Generate buffer and save
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `${sheet.carNumber}_${month}_${Date.now()}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
    
    return true;
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
}

// ------------------------------------------------------------
// Multi-Sheet Excel Export (FULL INDIVIDUAL EXPORT FORMAT)
// ------------------------------------------------------------
export async function exportAllSheetsToExcel(sheets, month) {
  try {
    const workbook = new ExcelJS.Workbook();

    // Month is expected as "YYYY-MM" (e.g. "2025-12")
    // Clamp any assignment periods to the sheet month boundaries (same as UI)
    const [yStr, mStr] = String(month || "").split("-");
    const sheetMonthStart = new Date(Number(yStr), Number(mStr) - 1, 1);
    const sheetMonthEnd = new Date(Number(yStr), Number(mStr), 0);
    sheetMonthStart.setHours(0, 0, 0, 0);
    sheetMonthEnd.setHours(23, 59, 59, 999);


//-------------------------------------------------------------
// BUILD SUMMARY DATA AUTOMATICALLY FROM ALL CAR SHEETS
//-------------------------------------------------------------
const summaryData = {
  expenses: {},            // Expense head → total
  bankIncome: [],          // Array of company rows
  cashIncome: [],          // Array of company rows
  bankIncomeMap: {},       // Internal helper
  cashIncomeMap: {},       // Internal helper
  totalIncome: 0,
  totalExpenses: 0,
  totalCashTrips: 0,
  posAmount: 0,
  cashWithDriver: 0,
  balance: 0
};

// De-duplicate sheets to avoid double-counting (but keep different periods for same car)
const uniqueSheets = [];
const seenSheetKeys = new Set();

(sheets || []).forEach((s) => {
  if (!s) return;

  // IMPORTANT:
  // carId/carNumber alone is NOT unique because one car can have multiple periods (your "-1" case).
  // So include tabName + start/end if available.
  const key = [
    s.sheetId ?? s.id ?? "",                 // strongest uniqueness if present
    s.carId ?? s.carNumber ?? "",
    s.tabName ?? "",                         // this usually carries the "-1" distinction from UI
    s.startDate ?? s.sheetStart ?? "",        // if you have period boundaries, include them
    s.endDate ?? s.sheetEnd ?? "",
    month ?? s.monthLabel ?? ""
  ].join("|");

  if (seenSheetKeys.has(key)) return;
  seenSheetKeys.add(key);
  uniqueSheets.push(s);
});



// -------------------------------------------------------------
// 1) EXPENSES SUMMARY
// -------------------------------------------------------------
uniqueSheets.forEach((sheet) => {
  (sheet?.expensesRows || []).forEach((row) => {
    // Skip summary rows in ALL known formats
    if (row?.isSummary) return;
    if (row?.rowType === "summary") return;

    const label = String(row?.label || "").trim();
    if (!label) return;

    // If someone accidentally left "TOTAL EXPENSES" as a normal row, skip it
    if (label.toUpperCase().includes("TOTAL EXPENSES")) return;

    if (!summaryData.expenses[label]) summaryData.expenses[label] = 0;

    // Some rows have `amount`, some have `total`
    const amt = Number(row?.amount ?? row?.total ?? 0);
    summaryData.expenses[label] += Number.isNaN(amt) ? 0 : amt;
  });
});


// -------------------------------------------------------------
// 2) BANK INCOME SUMMARY
// -------------------------------------------------------------
uniqueSheets.forEach(sheet => {
  sheet?.incomeRows?.forEach(row => {
    if (row.rowType === "summary") return;
    const company = row.company || "Unknown";

    if (!summaryData.bankIncomeMap[company]) {
      summaryData.bankIncomeMap[company] = {
        company,
        w1: 0, w2: 0, w3: 0, w4: 0, w5: 0, total: 0
      };
    }

    const obj = summaryData.bankIncomeMap[company];
    obj.w1 += row.week1 || 0;
    obj.w2 += row.week2 || 0;
    obj.w3 += row.week3 || 0;
    obj.w4 += row.week4 || 0;
    obj.w5 += row.week5 || 0;
    obj.total += row.total || 0;

    summaryData.totalIncome += row.total || 0;

    if (company.toUpperCase() === "POS") {
      summaryData.posAmount += row.total || 0;
    }
  });
});

// convert map → array
summaryData.bankIncome = Object.values(summaryData.bankIncomeMap);

// -------------------------------------------------------------
// 3) CASH TRIPS SUMMARY
// -------------------------------------------------------------
uniqueSheets.forEach(sheet => {
  sheet?.cashRows?.forEach(row => {
    if (row.rowType === "summary") return;
    const company = row.company || "Unknown";

    if (!summaryData.cashIncomeMap[company]) {
      summaryData.cashIncomeMap[company] = {
        company,
        w1: 0, w2: 0, w3: 0, w4: 0, w5: 0, total: 0
      };
    }

    const obj = summaryData.cashIncomeMap[company];
    obj.w1 += row.week1 || 0;
    obj.w2 += row.week2 || 0;
    obj.w3 += row.week3 || 0;
    obj.w4 += row.week4 || 0;
    obj.w5 += row.week5 || 0;
    obj.total += row.total || 0;

    summaryData.totalCashTrips += row.total || 0;
  });
});

// convert map → array
summaryData.cashIncome = Object.values(summaryData.cashIncomeMap);

// -------------------------------------------------------------
// 4) FINAL TOTALS
// -------------------------------------------------------------
summaryData.totalExpenses = Object.values(summaryData.expenses).reduce((a,b)=>a+b, 0);

summaryData.balance = summaryData.totalIncome - summaryData.totalExpenses;

summaryData.cashWithDriver = summaryData.totalCashTrips - summaryData.posAmount;


    workbook.creator = "Car Management System";
    workbook.created = new Date();

    
// ------------------------------------------------------------
// 0) ADD SUMMARY SHEET BEFORE ALL CAR SHEETS
// ------------------------------------------------------------
const summarySheet = workbook.addWorksheet("SUMMARY");

// Column widths (clean and balanced)
summarySheet.columns = [
  { width: 32 }, // A
  { width: 20 }, // B
  { width: 18 }, // C
  { width: 18 }, // D
  { width: 18 }, // E
  { width: 18 }  // F
];

// ---------------------------
// STYLES
// ---------------------------
const sectionTitle = {
  font: { bold: true, size: 14, color: { argb: "FF0D47A1" } }
};

const headerStyle = {
  font: { bold: true },
  fill: { type: "pattern", fgColor: { argb: "FFF0F0F0" }, pattern: "solid" },
  alignment: { horizontal: "center" }
};

const money = { numFmt: '"AED" #,##0.00' };

// ---------------------------
// TITLE
// ---------------------------
summarySheet.mergeCells("A1:F1");
summarySheet.getCell("A1").value = `MONTH SUMMARY — ${month}`;
summarySheet.getCell("A1").style = {
  font: { bold: true, size: 18 },
  alignment: { horizontal: "center" }
};

let r = 3;

// ============================================================
// 1) EXPENSES SUMMARY
// ============================================================
summarySheet.getCell(`A${r}`).value = "EXPENSES SUMMARY";
summarySheet.getCell(`A${r}`).style = sectionTitle;
r += 2;

// Headers
summarySheet.getCell(`A${r}`).value = "Expense Item";
summarySheet.getCell(`B${r}`).value = "Total";
summarySheet.getRow(r).eachCell(cell => (cell.style = headerStyle));
r++;

// Expense rows
for (const [label, total] of Object.entries(summaryData.expenses)) {
  summarySheet.getCell(`A${r}`).value = label;
  summarySheet.getCell(`B${r}`).value = total;
  summarySheet.getCell(`B${r}`).style = money;
  r++;
}

r += 2;

// ============================================================
// 2) BANK INCOME SUMMARY
// ============================================================
summarySheet.getCell(`A${r}`).value = "BANK INCOME SUMMARY";
summarySheet.getCell(`A${r}`).style = sectionTitle;
r += 2;

// Headers
["Company", "W1", "W2", "W3", "W4", "W5", "Total"].forEach((h, i) => {
  const cell = summarySheet.getCell(`${String.fromCharCode(65 + i)}${r}`);
  cell.value = h;
  cell.style = headerStyle;
});
r++;

// Rows
summaryData.bankIncome.forEach(row => {
  summarySheet.getCell(`A${r}`).value = row.company;

  ["w1", "w2", "w3", "w4", "w5", "total"].forEach((w, i) => {
    const c = summarySheet.getCell(`${String.fromCharCode(66 + i)}${r}`);
    c.value = row[w];
    c.style = money;
  });

  r++;
});

r += 3;

// ============================================================
// 3) INCOME & BALANCE SUMMARY
// ============================================================
summarySheet.getCell(`A${r}`).value = "INCOME & BALANCE SUMMARY";
summarySheet.getCell(`A${r}`).style = sectionTitle;
r += 2;

const incomeSummary = [
  ["TOTAL INCOME OF THE MONTH", summaryData.totalIncome],
  ["TOTAL EXPENSES", summaryData.totalExpenses],
  ["NET BALANCE (Income – Expenses)", summaryData.balance],
  ["CASH WITH DRIVER (Cash – POS)", summaryData.cashWithDriver]
];

incomeSummary.forEach(([label, value]) => {
  summarySheet.getCell(`A${r}`).value = label;
  summarySheet.getCell(`A${r}`).style = { font: { bold: true } };

  const c = summarySheet.getCell(`B${r}`);
  setAedCell(c, value, { dashZero: true });
  c.font = { ...(c.font || {}), bold: true };

  r++;
});

r += 3;

// ============================================================
// 4) CASH TRIPS SUMMARY
// ============================================================
summarySheet.getCell(`A${r}`).value = "CASH TRIPS SUMMARY";
summarySheet.getCell(`A${r}`).style = sectionTitle;
r += 2;

// Headers
["Company", "W1", "W2", "W3", "W4", "W5", "Total"].forEach((h, i) => {
  const cell = summarySheet.getCell(`${String.fromCharCode(65 + i)}${r}`);
  cell.value = h;
  cell.style = headerStyle;
});
r++;

// Rows
summaryData.cashIncome.forEach(row => {
  summarySheet.getCell(`A${r}`).value = row.company;

  ["w1", "w2", "w3", "w4", "w5", "total"].forEach((w, i) => {
    const c = summarySheet.getCell(`${String.fromCharCode(66 + i)}${r}`);
    c.value = row[w];
    c.style = money;
  });

  r++;
});


r += 3;

// ============================================================
// 5) BALANCE CASH WITH DRIVER
// ============================================================
summarySheet.getCell(`A${r}`).value = "CASH WITH DRIVER SUMMARY";
summarySheet.getCell(`A${r}`).style = sectionTitle;
r += 2;

[
  ["TOTAL CASH TRIPS", summaryData.totalCashTrips],
  ["LESS: POS AMOUNT RECEIVED IN BANK", summaryData.posAmount],
  ["BALANCE CASH WITH DRIVER", summaryData.cashWithDriver]
].forEach(([label, value]) => {
  summarySheet.getCell(`A${r}`).value = label;
  summarySheet.getCell(`A${r}`).style = { font: { bold: true } };

  const c = summarySheet.getCell(`B${r}`);
  c.value = value;
  c.style = money;

  r++;
});


    for (const sheet of sheets) {
      if (!sheet || !sheet.carNumber) continue;

      // -----------------------------------------------
      // Create worksheet using car number as tab name
      // -----------------------------------------------
      const baseName = `${sheet.carNumber} ${sheet.tabName || ""}`.trim(); 
// If you only want car number, use: const baseName = sheet.carNumber;

const wsName = makeUniqueWorksheetName(workbook, baseName);
const mainSheet = workbook.addWorksheet(wsName);

      // --------------------------------------------------
// FIX COLUMN WIDTHS (same as individual export)
// --------------------------------------------------
mainSheet.columns = [
  { width: 25 }, // A
  { width: 22 }, // B
  { width: 14 }, // C
  { width: 14 }, // D
  { width: 14 }, // E
  { width: 14 }, // F
  { width: 14 }, // G
  { width: 18 }  // H
];

// Set header & title row heights (same as individual)
mainSheet.getRow(1).height = 28;
mainSheet.getRow(2).height = 22;
mainSheet.getRow(3).height = 8;   // blank spacing row


      // -----------------------------------------------
      // STYLES
      // -----------------------------------------------
      const headerStyle = { 
        font: { bold: true, size: 14, color: { argb: "FFFFFFFF" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D47A1" } },
        alignment: { horizontal: "center", vertical: "middle" }
      };

      const subHeaderStyle = {
        font: { bold: true, size: 12, color: { argb: "FF333333" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } },
        alignment: { horizontal: "center", vertical: "middle" }
      };

      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: "FF333333" } },
        alignment: { horizontal: "center", vertical: "middle" }
      };

      const tableHeaderStyle = {
        font: { bold: true, color: { argb: "FF333333" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" }
        }
      };

      const currencyStyle = {
        numFmt: '"AED" #,##0.00;[Red]"AED" -#,##0.00'
      };

      // ---------------------------------------------------------------------------------------------------
      // START WRITING SHEET CONTENT (IDENTICAL TO exportSheetToExcel)
      // ---------------------------------------------------------------------------------------------------

      let currentRow = 1;

      // ================================
      // TITLE
      // ================================
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "CAR PROFIT & LOSS STATEMENT";
      mainSheet.getCell(`A${currentRow}`).style = titleStyle;
      currentRow++;

      // Month Label
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = sheet.monthLabel || month;
      mainSheet.getCell(`A${currentRow}`).style = { ...titleStyle, font: { ...titleStyle.font, size: 12 }};
      currentRow += 2;

      // ================================
      // CAR DETAILS
      // ================================
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "Car Details";
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      currentRow++;

      const carDetails = [
        ["Car Number:", sheet.carNumber, "Car Model:", sheet.carModel || "N/A"],
        ["Company:", sheet.company || "N/A", "Plan Type:", sheet.planType || "N/A"]
      ];

      carDetails.forEach(r => {
        mainSheet.getCell(`A${currentRow}`).value = r[0];
        mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }};
        mainSheet.getCell(`B${currentRow}`).value = r[1];

        mainSheet.getCell(`E${currentRow}`).value = r[2];
        mainSheet.getCell(`E${currentRow}`).style = { font: { bold: true }};
        mainSheet.getCell(`F${currentRow}`).value = r[3];
        currentRow++;
      });

      currentRow++;

      // ================================
      // DRIVER ASSIGNMENT PERIODS
      // ================================
      if (sheet.driverPeriods && Object.keys(sheet.driverPeriods).length > 0) {
        mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
        mainSheet.getCell(`A${currentRow}`).value = "Driver Assignment Periods";
        mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
        currentRow++;

        const headers = ["Driver ID", "Driver Name", "Start Date", "End Date", "Days Worked"];
        headers.forEach((h, idx) => {
          const c = mainSheet.getCell(`${String.fromCharCode(65 + idx)}${currentRow}`);
          c.value = h;
          c.style = tableHeaderStyle;
        });
        currentRow++;

        Object.entries(sheet.driverPeriods || {}).forEach(([driverId, periodsOrObj]) => {
  const periods = Array.isArray(periodsOrObj) ? periodsOrObj : [periodsOrObj];

  periods.forEach((p) => {
    if (!p) return;

    const rawStart = p.startDate || p.start;
    const rawEnd = p.endDate || p.end;

    mainSheet.getCell(`A${currentRow}`).value = driverId;
    mainSheet.getCell(`B${currentRow}`).value = p.driverName || driverId;

    // Parse + clamp to current sheet month boundaries (same logic as UI)
    const sParsed = rawStart ? new Date(rawStart) : null;
    const eParsed = rawEnd ? new Date(rawEnd) : null;

    let startDate = sParsed && !isNaN(sParsed.getTime()) ? sParsed : null;
    let endDate = eParsed && !isNaN(eParsed.getTime()) ? eParsed : null;

    // If open-ended, clamp to sheet boundaries
    if (!startDate) startDate = new Date(sheetMonthStart);
    if (!endDate) endDate = new Date(sheetMonthEnd);

    // Clamp within sheet month
    if (startDate < sheetMonthStart) startDate = new Date(sheetMonthStart);
    if (endDate > sheetMonthEnd) endDate = new Date(sheetMonthEnd);

    // Write start/end as date-only (avoid timezone hour offsets causing +1 day)
    const displayStart = new Date(startDate);
    const displayEnd = new Date(endDate);
    displayStart.setHours(12, 0, 0, 0);
displayEnd.setHours(12, 0, 0, 0);


    mainSheet.getCell(`C${currentRow}`).value = displayStart;
    mainSheet.getCell(`C${currentRow}`).numFmt = "dd-mmm-yyyy";

    mainSheet.getCell(`D${currentRow}`).value = displayEnd;
    mainSheet.getCell(`D${currentRow}`).numFmt = "dd-mmm-yyyy";

    // Inclusive day count (no 32-day bug)
    let days = 0;
    if (!isNaN(displayStart.getTime()) && !isNaN(displayEnd.getTime()) && displayEnd >= displayStart) {
      days = Math.floor((displayEnd.getTime() - displayStart.getTime()) / 86400000) + 1;
    }
    mainSheet.getCell(`E${currentRow}`).value = days;

    currentRow++;
  });
});


        currentRow += 2;
      }

      // ================================
      // EXPENSES — FULL LOGIC SAME AS INDIVIDUAL EXPORT
      // ================================
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "EXPENSES";
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      currentRow++;

      const expenseHeaders = ["Expense Item", "", "", "", "", "", "", "Amount"];
      expenseHeaders.forEach((h, idx) => {
        const cell = mainSheet.getCell(`${String.fromCharCode(65 + idx)}${currentRow}`);
        cell.value = h;
        cell.style = tableHeaderStyle;
      });
      currentRow++;

      if (sheet.expensesRows?.length > 0) {
        sheet.expensesRows.forEach(row => {
          const label = (row.label || "").toUpperCase();

          if (row.rowType === "summary") {
            mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);
            mainSheet.getCell(`A${currentRow}`).value = row.label;
            mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }};
            const cell = mainSheet.getCell(`H${currentRow}`);
            setAedCell(cell, row.total, { dashZero: true });
            cell.font = { ...(cell.font || {}), bold: true, color: { argb: "FFFF0000" } };
          }

          else if (label.includes("TRAFFIC") || label.includes("FINE DATE")) {
            mainSheet.getCell(`A${currentRow}`).value = row.label || "";

            for (let i = 1; i <= 6; i++) {
              const col = String.fromCharCode(65 + i); // B..G
              mainSheet.getCell(`${col}${currentRow}`).value = row[`col${i}`] || "";
            }

            const amountCell = mainSheet.getCell(`H${currentRow}`);
            setAedCell(amountCell, row.total, { dashZero: true });
          }

          else {
            mainSheet.getCell(`A${currentRow}`).value = row.label || "";
            mainSheet.mergeCells(`B${currentRow}:G${currentRow}`);
            const detailCell = mainSheet.getCell(`B${currentRow}`);
            detailCell.value = getExpenseDownloadDetail(row);
            detailCell.alignment = { wrapText: true, vertical: "top" };

            const c = mainSheet.getCell(`H${currentRow}`);
            setAedCell(c, row.total, { dashZero: true });
          }

          currentRow++;
        });
      } else {
        mainSheet.getCell(`A${currentRow}`).value = "No expenses data";
        currentRow++;
      }

      currentRow += 2;

      // ================================
      // INCOME SECTION (FULL COPY)
      // ================================
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "INCOME";
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      currentRow++;

      const incomeHeaders = ["Platform", "Driver", "Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Total"];
      incomeHeaders.forEach((h, idx) => {
        const cell = mainSheet.getCell(`${String.fromCharCode(65 + idx)}${currentRow}`);
        cell.value = h;
        cell.style = tableHeaderStyle;
      });
      currentRow++;

      if (sheet.incomeRows?.length > 0) {
        sheet.incomeRows.forEach(row => {
          if (row.rowType === "summary") {
            mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);
            mainSheet.getCell(`A${currentRow}`).value = row.label;
            mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }};
            const c = mainSheet.getCell(`H${currentRow}`);
            setAedCell(c, row.total, { dashZero: true });
            c.font = { ...(c.font || {}), bold: true };
          } else {
            mainSheet.getCell(`A${currentRow}`).value = row.company || "";
            mainSheet.getCell(`B${currentRow}`).value = row.driver || "";

            ["week1","week2","week3","week4","week5"].forEach((w,i) => {
              const cell = mainSheet.getCell(`${String.fromCharCode(67+i)}${currentRow}`);
              cell.value = row[w] || 0;
              cell.style = currencyStyle;
            });

            const t = mainSheet.getCell(`H${currentRow}`);
            setAedCell(t, row.total, { dashZero: true });
            t.font = { ...(t.font || {}), bold: true };
          }
          currentRow++;
        });
      }

      currentRow += 2;

      // ================================
      // CASH SECTION (FULL COPY)
      // ================================
      mainSheet.mergeCells(`A${currentRow}:H${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "CASH TRIPS";
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      currentRow++;

      const cashHeaders = [
  "Platform",
  "Driver",
  "Week 1",
  "Week 2",
  "Week 3",
  "Week 4",
  "Week 5",
  "Total"
];

cashHeaders.forEach((h, i) => {
  const cell = mainSheet.getCell(`${String.fromCharCode(65 + i)}${currentRow}`);
  cell.value = h;
  cell.style = tableHeaderStyle;
});

      currentRow++;

      if(sheet.cashRows?.length>0){
        sheet.cashRows.forEach(row=>{
          if(row.rowType==="summary"){
            mainSheet.mergeCells(`A${currentRow}:G${currentRow}`);
            mainSheet.getCell(`A${currentRow}`).value=row.label;
            mainSheet.getCell(`A${currentRow}`).style={font:{bold:true}};
            const c=mainSheet.getCell(`H${currentRow}`);
            c.value=row.total||0;
            c.style={...currencyStyle,font:{bold:true}};
          } else {
            mainSheet.getCell(`A${currentRow}`).value=row.company||"";
            mainSheet.getCell(`B${currentRow}`).value=row.driver||"";

            ["week1","week2","week3","week4","week5"].forEach((w,i)=>{
              const cell=mainSheet.getCell(`${String.fromCharCode(67+i)}${currentRow}`);
              cell.value=row[w]||0;
              cell.style=currencyStyle;
            });

            const t=mainSheet.getCell(`H${currentRow}`);
            t.value=row.total||0;
            t.style={...currencyStyle,font:{bold:true}};
          }
          currentRow++;
        });
      }

      currentRow += 2;

      // ================================
      // SUMMARY SECTION — FINAL VERSION
      // ================================
      mainSheet.mergeCells(`A${currentRow}:B${currentRow}`);
      mainSheet.getCell(`A${currentRow}`).value = "SUMMARY";
      mainSheet.getCell(`A${currentRow}`).style = subHeaderStyle;
      currentRow++;

      const totalIncomeMonth = sheet.totalIncomeOfMonth || sheet.totalIncome || 0;
      const totalExpenses = sheet.totalExpenses || 0;
      const netProfit = totalIncomeMonth - totalExpenses;

      const summaryData = [
        ["Total Income (Bank Only):", totalIncomeMonth],
        ["Total Expenses:", totalExpenses],
        ["Net Profit:", netProfit]
      ];

      summaryData.forEach(([label, value], index) => {
        mainSheet.getCell(`A${currentRow}`).value = label;
        mainSheet.getCell(`A${currentRow}`).style = { font: { bold: true }};

        const cell = mainSheet.getCell(`B${currentRow}`);
        cell.value = value;
        cell.style = {
          ...currencyStyle,
          font: { bold: true },
          fill: index === 2 ? { type:"pattern",pattern:"solid",fgColor:{argb:"FFE8F5E8"} } : undefined
        };

        if (index === 2 && value < 0) {
          cell.font = { bold: true, color:{ argb:"FFFF0000" }};
        }
        currentRow++;
      });
    }

    // -----------------------------------------------
    // SAVE FILE
    // -----------------------------------------------
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Car_PL_All_${month}.xlsx`);

    console.log("✅ Multi-sheet export completed");
  } catch (err) {
    console.error("❌ Export Error:", err);
    alert("Export failed: " + err.message);
  }
}
